﻿import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuth } from '@/composables/useAuth'
import type { ApiResponse, PaginatedResponse } from '@/services/types'
import { isApiOk } from '@/services/types'
import { extractErrorMessage } from '@/utils/errorUtils'
import { readPasswordPolicy } from '@/utils/passwordRule'
import {
  setThemePreference,
  setFontSizePreference,
  applyThemeToDOM,
  applyFontSizeToDOM,
} from '@/utils/preferences'
import type {
  UserProfile,
  UpdateProfileDto,
  ChangePasswordDto,
  ResetPasswordByCodeDto,
  PasswordPolicy,
  OrgMembershipApplication,
  OrgMembership,
  ApplyForOrgDto,
  LoginDevice,
  FeedbackTicket,
  CreateFeedbackDto,
  FaqItem,
  AccountDeletionRequest,
  WechatBindStatus,
  DeletionCheckResult,
  RequestDeletionDto,
  CancelDeletionDto,
} from '@/types/user-settings'
import {
  getUserProfile,
  updateUserProfile,
  uploadAvatar,
  changePassword as changePasswordApi,
  getPasswordPolicy,
  sendResetCode,
  resetPasswordByCode,
  listMyApplications,
  applyForOrg,
  withdrawApplication,
  requestLeaveOrg,
  listLoginDevices,
  terminateDevice,
  terminateAllDevices,
  getWechatBindStatus,
  initiateWechatBind,
  confirmWechatBind,
  unbindWechat,
  listFaqs,
  submitFeedback,
  listMyFeedbacks,
  checkDeletionPrerequisites,
  sendDeletionCode,
  requestAccountDeletion,
  cancelAccountDeletion,
} from '@/services/userSettingsApi'
import { systemAvatarsService } from '@/services/system-avatars'

export const useAccountSettingsStore = defineStore('accountSettings', () => {
  // ========== State ==========
  const loading = ref(false)
  const error = ref<string | null>(null)

  const profile = ref<UserProfile | null>(null)
  const myApplications = ref<OrgMembershipApplication[]>([])
  const currentMembership = ref<OrgMembership | null>(null)
  const devices = ref<LoginDevice[]>([])
  /** 修复项：#11/#18 设备列表缓存时间戳（ms）。5 分钟内重复进入不重新请求。 */
  const devicesFetchedAt = ref<number>(0)
  /** 修复项：#11 设备列表缓存 TTL（5 分钟 = 300_000 ms） */
  const DEVICES_TTL_MS = 5 * 60 * 1000
  const myFeedbacks = ref<FeedbackTicket[]>([])
  const feedbacksTotal = ref(0)
  const deletionRequest = ref<AccountDeletionRequest | null>(null)
  /** 后端下发的密码规则；null 表示尚未取到（面板降级为「只判两次一致」，不套本地兜底规则） */
  const passwordPolicy = ref<PasswordPolicy | null>(null)

  // ========== Getters ==========
  /**
   * 判断用户是否已入驻正式单位（非个人体验空间）。
   * 个人体验空间用户也有 organizationId，因此需通过 organizationType 区分。
   */
  const hasOrganization = computed(() => {
    const type = profile.value?.organizationType
    return !!profile.value?.organizationId && type !== 'personal'
  })

  // ========== Helpers ==========
  function setLoading(value: boolean) {
    loading.value = value
  }

  function setError(msg: string | null) {
    error.value = msg
  }

  function clearError() {
    error.value = null
  }

  async function wrapAction<T>(
    action: () => Promise<T>,
    errorMsg: string,
    options?: { setLoadingState?: boolean; onSuccess?: (result: T) => void }
  ): Promise<T | { code: 500, data: undefined, message: string }> {
    if (options?.setLoadingState !== false) setLoading(true)
    clearError()
    try {
      const result = await action()
      options?.onSuccess?.(result)
      return result
    } catch (e) {
      // 用统一提取规则：业务 4xx 透出后端 message，网络/超时/5xx 转友好文案，
      // 避免 Axios 原始英文消息（如 "Request failed with status code 429"）直达用户
      const msg = extractErrorMessage(e, errorMsg)
      setError(msg)
      return { code: 500, data: undefined, message: msg }
    } finally {
      if (options?.setLoadingState !== false) setLoading(false)
    }
  }

  // ========== Profile Actions ==========
  async function fetchProfile(): Promise<ApiResponse<UserProfile>> {
    return wrapAction(
      async () => {
        const res = await getUserProfile()
        if (res.data) {
          profile.value = res.data
          // 修复项：从后端用户资料恢复主题和字体大小偏好。
          // 如果后端有值，覆盖 localStorage 并应用到 DOM（实现多设备同步）；
          // 如果后端无值，保持 localStorage 现有值（兼容首次登录或旧数据）。
          if (res.data.theme) {
            setThemePreference(res.data.theme)
            applyThemeToDOM(res.data.theme)
          }
          if (res.data.fontSize) {
            setFontSizePreference(res.data.fontSize)
            applyFontSizeToDOM(res.data.fontSize)
          }
        }
        return res
      },
      '获取资料失败'
    ) as Promise<ApiResponse<UserProfile>>
  }

  async function updateProfile(dto: UpdateProfileDto): Promise<ApiResponse<UserProfile>> {
    return wrapAction(
      async () => {
        const res = await updateUserProfile(dto)
        if (res.data) {
          profile.value = res.data
          // 顶部栏/侧边栏读的是登录态里的 avatarUrl，必须经 updateCurrentUser 落进去；
          // 直接给 auth.state.currentUser 赋值会被 Vue 的 readonly 代理静默丢弃。
          const auth = useAuth()
          auth.updateCurrentUser({
            displayName: res.data.displayName,
            // 只在拿到值时才写入：MinIO 关闭时后端 avatarUrl 返回 null，而显式传 undefined 会覆盖会话里的真值
            ...(res.data.avatarUrl ? { avatarUrl: res.data.avatarUrl } : {}),
            ...(res.data.region ? { region: res.data.region } : {}),
          })
        }
        return res
      },
      '更新资料失败'
    ) as Promise<ApiResponse<UserProfile>>
  }

  // 修复项：新增 savePreferences action，用于将主题/字体大小偏好静默同步到后端。
  // 采用"本地优先"策略：无论后端同步是否成功，本地体验和 localStorage 都已更新；
  // 仅在网络失败时输出 warning，避免打断用户操作。
  async function savePreferences(prefs: { theme?: 'light' | 'dark' | 'system'; fontSize?: 'normal' | 'large' }): Promise<void> {
    try {
      const res = await updateUserProfile(prefs)
      if (res.data) {
        profile.value = res.data
      }
    } catch (e) {
      console.warn('同步偏好到后端失败:', e)
    }
  }


  /**
   * 把接口现签的头像地址同步进登录态，供顶部栏与侧边栏即时渲染。
   * 必须走 updateCurrentUser：它会重建 currentUser 并写回 localStorage，
   * 而 auth.state 是 readonly 代理，直接赋值是静默空操作。
   */
  function syncAvatarToSession(avatarUrl: string) {
    useAuth().updateCurrentUser({ avatarUrl })
  }

  async function uploadUserAvatar(file: File): Promise<ApiResponse<{ avatarUrl: string }>> {
    return wrapAction(
      async () => {
        const res = await uploadAvatar(file)
        if (res.data) {
          if (profile.value) profile.value.avatarUrl = res.data.avatarUrl
          syncAvatarToSession(res.data.avatarUrl)
        }
        return res
      },
      '上传头像失败'
    ) as Promise<ApiResponse<{ avatarUrl: string }>>
  }

  /**
   * 选用系统预设头像：只向后端提交 code，成功后用响应里现签的 URL 刷新本地形象。
   * 失败时不改写 profile，用户保持原头像。
   */
  async function selectPresetAvatar(code: string): Promise<ApiResponse<{ avatarUrl: string }>> {
    return wrapAction(
      async () => {
        const res = await systemAvatarsService.selectPresetAvatar(code)
        if (res.data) {
          if (profile.value) profile.value.avatarUrl = res.data.avatarUrl
          syncAvatarToSession(res.data.avatarUrl)
        }
        return res
      },
      '设置头像失败'
    ) as Promise<ApiResponse<{ avatarUrl: string }>>
  }

  // ========== Password Actions ==========
  /** 拉取密码规则（复杂度与展示文案的唯一来源，前端不再复写正则） */
  async function fetchPasswordPolicy(): Promise<ApiResponse<PasswordPolicy>> {
    return wrapAction(
      async () => {
        const res = await getPasswordPolicy()
        // 字段不全的策略不予采用（视为未取到），面板走「不本地猜规则」降级而非拿半套规则静默放行
        if (isApiOk(res)) passwordPolicy.value = readPasswordPolicy(res.data)
        return res
      },
      '获取密码规则失败',
      { setLoadingState: false }
    ) as Promise<ApiResponse<PasswordPolicy>>
  }

  async function changePassword(dto: ChangePasswordDto): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => {
        const res = await changePasswordApi(dto)
        if (!isApiOk(res)) setError(res.message ?? '修改密码失败')
        return res
      },
      '修改密码失败'
    ) as Promise<ApiResponse<void>>
  }

  async function sendPasswordResetCode(mobile: string): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => sendResetCode(mobile),
      '发送验证码失败',
      { setLoadingState: false }
    ) as Promise<ApiResponse<void>>
  }

  async function resetPassword(dto: ResetPasswordByCodeDto): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => {
        const res = await resetPasswordByCode(dto)
        if (!isApiOk(res)) setError(res.message ?? '重置密码失败')
        return res
      },
      '重置密码失败'
    ) as Promise<ApiResponse<void>>
  }

  // ========== Organization Actions ==========
  async function fetchMyApplications(): Promise<ApiResponse<OrgMembershipApplication[]>> {
    return wrapAction(
      async () => {
        const res = await listMyApplications()
        if (res.data) myApplications.value = res.data
        return res
      },
      '获取申请列表失败'
    ) as Promise<ApiResponse<OrgMembershipApplication[]>>
  }

  async function applyForOrganization(dto: ApplyForOrgDto): Promise<ApiResponse<OrgMembershipApplication>> {
    return wrapAction(
      async () => {
        const res = await applyForOrg(dto)
        if (res.data) myApplications.value.unshift(res.data)
        return res
      },
      '提交申请失败'
    ) as Promise<ApiResponse<OrgMembershipApplication>>
  }

  async function withdrawOrgApplication(appId: string): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => {
        const res = await withdrawApplication(appId)
        if (isApiOk(res)) {
          const app = myApplications.value.find((a) => a.id === appId)
          if (app) app.status = 'withdrawn'
        }
        return res
      },
      '撤销申请失败'
    ) as Promise<ApiResponse<void>>
  }

  async function leaveOrganization(reason?: string): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => {
        const res = await requestLeaveOrg({ reason })
        if (isApiOk(res)) {
          // Note: actual org dissociation now requires admin approval
          // The profile will be updated when the leave is approved
        }
        return res
      },
      '申请退出失败'
    ) as Promise<ApiResponse<void>>
  }

  // P8-fix: 通知偏好相关状态与操作已迁移到 useNotificationStore，本 store 不再维护。
  // 原 Notification Actions 已移除，避免 TypeScript 未使用变量警告。

  // ========== Device Actions ==========

  /**
   * 修复项：#11/#18 store 缺少缓存 —— 加 5 分钟 TTL。
   * 命中缓存时直接返回当前 devices（不发起网络请求）；
   * force=true 时强制重新拉取（如「退出全部」后希望立即看到最新数据）。
   */
  async function fetchDevices(force = false): Promise<ApiResponse<LoginDevice[]>> {
    // 修复项：#11 命中缓存（仅在有数据时跳过请求，避免首次加载空闪屏）
    const now = Date.now()
    if (!force && devicesFetchedAt.value > 0 && now - devicesFetchedAt.value < DEVICES_TTL_MS) {
      return { code: 200, data: devices.value }
    }
    return wrapAction(
      async () => {
        const res = await listLoginDevices()
        if (res.data) {
          devices.value = res.data
          devicesFetchedAt.value = Date.now()
        }
        return res
      },
      '获取设备列表失败'
    ) as Promise<ApiResponse<LoginDevice[]>>
  }

  /**
   * 修复项：TASK-029 「已退出」标签不显示 —— 使用 isApiOk 统一判断成功状态，
   * 并在本地软更新后强制刷新列表作为兜底，确保标签即时渲染。
   */
  async function terminateDeviceSession(deviceId: string): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => {
        const res = await terminateDevice(deviceId)
        if (isApiOk(res)) {
          // 软更新：保留记录，标记为 terminated
          const idx = devices.value.findIndex((d) => d.id === deviceId)
          if (idx !== -1) {
            devices.value = devices.value.map((d, i) =>
              i === idx ? { ...d, sessionStatus: 'terminated' as const } : d
            )
          }
          // 兜底刷新：确保与后端状态同步并触发重新渲染
          await fetchDevices(true)
        }
        return res
      },
      '退出设备失败'
    ) as Promise<ApiResponse<void>>
  }

  /**
   * 修复项：#7 一键退出全部其他设备 —— 行业标准安全功能。
   * 成功后强制重新拉取列表以反映最新状态。
   */
  async function terminateAllOtherDevices(): Promise<ApiResponse<{ terminatedCount: number }>> {
    return wrapAction(
      async () => {
        const res = await terminateAllDevices()
        if (isApiOk(res)) {
          // 强制刷新以反映后端的批量更新
          await fetchDevices(true)
        }
        return res
      },
      '退出全部设备失败'
    ) as Promise<ApiResponse<{ terminatedCount: number }>>
  }

  // ========== Wechat Actions ==========
  async function fetchWechatStatus(): Promise<ApiResponse<WechatBindStatus>> {
    return wrapAction(
      async () => getWechatBindStatus(),
      '获取微信状态失败',
      { setLoadingState: false }
    ) as Promise<ApiResponse<WechatBindStatus>>
  }

  async function initiateWechatBinding(): Promise<ApiResponse<{ authUrl: string; state: string }>> {
    return wrapAction(
      async () => initiateWechatBind(),
      '发起微信绑定失败',
      { setLoadingState: false }
    ) as Promise<ApiResponse<{ authUrl: string; state: string }>>
  }

  async function confirmWechatBinding(code: string, state: string): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => {
        const res = await confirmWechatBind(code, state)
        if (isApiOk(res) && profile.value) profile.value.wechatBound = true
        return res
      },
      '确认微信绑定失败',
      { setLoadingState: false }
    ) as Promise<ApiResponse<void>>
  }

  async function unbindWechatAccount(): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => {
        const res = await unbindWechat()
        if (isApiOk(res) && profile.value) {
          profile.value.wechatBound = false
          profile.value.wechatOpenId = undefined
        }
        return res
      },
      '解绑微信失败',
      { setLoadingState: false }
    ) as Promise<ApiResponse<void>>
  }

  // ========== Feedback Actions ==========
  async function fetchFeedbacks(page = 1, pageSize = 10): Promise<PaginatedResponse<FeedbackTicket>> {
    return wrapAction(
      async () => {
        const res = await listMyFeedbacks(page, pageSize)
        myFeedbacks.value = res.items
        feedbacksTotal.value = res.total
        return res
      },
      '获取反馈列表失败'
    ) as Promise<PaginatedResponse<FeedbackTicket>>
  }

  async function createFeedback(dto: CreateFeedbackDto): Promise<ApiResponse<FeedbackTicket>> {
    return wrapAction(
      async () => {
        const res = await submitFeedback(dto)
        if (res.data) {
          myFeedbacks.value.unshift(res.data)
          feedbacksTotal.value++
        }
        return res
      },
      '提交反馈失败'
    ) as Promise<ApiResponse<FeedbackTicket>>
  }

  async function fetchFaqs(category?: string): Promise<ApiResponse<FaqItem[]>> {
    return wrapAction(
      async () => listFaqs(category),
      '获取常见问题失败',
      { setLoadingState: false }
    ) as Promise<ApiResponse<FaqItem[]>>
  }

  // ========== Account Deletion Actions ==========
  async function checkDeletionStatus(): Promise<ApiResponse<DeletionCheckResult>> {
    return wrapAction(
      async () => {
        const res = await checkDeletionPrerequisites()
        if (res.data) {
          // 刷新恢复修复：进行中的申请随检查结果回填 store，
          // 否则页面刷新后「处理中」面板消失、用户无法撤销注销
          deletionRequest.value = res.data.activeRequest ?? null
        }
        return res
      },
      '检查注销条件失败'
    ) as Promise<ApiResponse<DeletionCheckResult>>
  }

  async function sendDeletionVerificationCode(): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => sendDeletionCode(),
      '发送验证码失败',
      { setLoadingState: false }
    ) as Promise<ApiResponse<void>>
  }

  async function requestDeletion(dto: RequestDeletionDto): Promise<ApiResponse<AccountDeletionRequest>> {
    return wrapAction(
      async () => {
        const res = await requestAccountDeletion(dto)
        if (res.data) deletionRequest.value = res.data
        return res
      },
      '提交注销申请失败'
    ) as Promise<ApiResponse<AccountDeletionRequest>>
  }

  async function cancelDeletion(dto: CancelDeletionDto): Promise<ApiResponse<void>> {
    return wrapAction(
      async () => {
        const res = await cancelAccountDeletion(dto)
        if (isApiOk(res)) deletionRequest.value = null
        return res
      },
      '撤销注销申请失败'
    ) as Promise<ApiResponse<void>>
  }

  return {
    // State
    loading,
    error,
    profile,
    myApplications,
    currentMembership,
    devices,
    myFeedbacks,
    feedbacksTotal,
    deletionRequest,
    passwordPolicy,
    // Getters
    hasOrganization,
    // Actions
    clearError,
    fetchProfile,
    updateProfile,
    savePreferences,
    uploadUserAvatar,
    selectPresetAvatar,
    changePassword,
    fetchPasswordPolicy,
    sendPasswordResetCode,
    resetPassword,
    fetchMyApplications,
    applyForOrganization,
    withdrawOrgApplication,
    leaveOrganization,
    fetchDevices,
    terminateDeviceSession,
    terminateAllOtherDevices,
    fetchWechatStatus,
    initiateWechatBinding,
    confirmWechatBinding,
    unbindWechatAccount,
    fetchFeedbacks,
    createFeedback,
    fetchFaqs,
    checkDeletionStatus,
    sendDeletionVerificationCode,
    requestDeletion,
    cancelDeletion,
  }
})
